import { faChevronCircleRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "client/src/components/Button/Button";

import { Body5 } from "client/src/components/Typography/Typography";
import { useSlobAuth } from "client/src/hooks/auth";
import { useGetClientByID, useGetUserClients } from "client/src/hooks/client";
import { PrintHidden } from "client/src/utils/print";
import clsx from "clsx";
import { RouteData } from "shared/config/routeData";
import { getIsInternalUser, getIsBroker, getIsBenAdmin } from "shared/rbac/rbac";

import * as styles from "./userBanner.module.css";

type UserBannerProps = {
  clientId: string;
};

export function UserBanner({ clientId }: UserBannerProps) {
  const { authUser, isLoading: isLoadingAuth } = useSlobAuth();
  const { isLoading: isLoadingClient, data: client } = useGetClientByID(clientId);

  const ready = authUser && !isLoadingAuth && !isLoadingClient;
  const isInternalUser = getIsInternalUser(authUser);
  const isBroker = getIsBroker(authUser);
  const isBA = getIsBenAdmin(authUser);

  const { data: userClients } = useGetUserClients({
    userId: isBA ? authUser.id || "" : "",
    pageSize: 1,
    page: 1,
    search: "",
    completedSetup: true,
    ...(isBA
      ? {
          phaseIds: [
            "WELCOME",
            "INFO_GATHERING",
            "REVIEW_AND_VALIDATE",
            "PLAN_APPROVAL",
            "CONTRACTS_AND_BILLING",
            "ONBOARDING_COMPLETE",
          ],
        }
      : {}),
  });

  if (!ready || !client || !(isInternalUser || isBroker || isBA)) {
    return null;
  }

  if (isBA) {
    if (userClients == null || userClients.meta.count === 1) {
      return null;
    }
  }

  return (
    <PrintHidden>
      <div
        className={clsx(styles.userBanner, {
          [styles.internalUser]: isInternalUser,
          [styles.broker]: !isInternalUser && isBroker,
        })}
      >
        <span className={styles.text}>
          {isInternalUser ? (
            <Body5 colorPrimary>You’re viewing {client.name} as an internal user. </Body5>
          ) : (
            isBroker && <Body5 colorPrimary>You’re viewing {client.name} as a broker. </Body5>
          )}
          <Button
            type="text-only-link"
            size="small"
            to={
              isInternalUser
                ? RouteData.homeInternal.getPath()
                : isBroker
                ? RouteData.homeBroker.getPath()
                : isBA
                ? RouteData.homeBenAdmin.getPath()
                : RouteData.index.getPath()
            }
          >
            See all clients <FontAwesomeIcon icon={faChevronCircleRight} className="ml-[4px]" />
          </Button>{" "}
          {isInternalUser && (
            <Button
              type="text-only-link"
              size="small"
              to={RouteData.clientDetail.getPath(clientId)}
            >
              Manage client <FontAwesomeIcon icon={faChevronCircleRight} className="ml-[4px]" />
            </Button>
          )}
        </span>
      </div>
    </PrintHidden>
  );
}
